import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Botao from '../../components/Botao';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { mdiContentCopy, mdiRefresh, mdiTrashCan, mdiLoading, mdiKeyOutline } from '@mdi/js';
import Icon from '../../components/Icon';

const SecaoChavesApi = () => {
    const queryClient = useQueryClient();

    const { data: chave, isLoading } = useQuery(
        'configuracoes/chaves-api/fluxo',
        async () => {
            const response = await axios.get('/api/configuracoes/chaves-api/fluxo', {
                withCredentials: true,
            });
            return response.data.result.valor as string | null;
        },
        {
            onError: () => toast.error('Erro ao carregar a chave de API'),
            refetchOnWindowFocus: false,
        }
    );

    const gerarMutation = useMutation(
        async () => {
            const response = await axios.post('/api/configuracoes/chaves-api/fluxo/gerar', {}, {
                withCredentials: true,
            });
            return response.data.result.valor as string;
        },
        {
            onSuccess: () => {
                toast.success('Nova chave gerada. Integrações usando a chave anterior deixarão de funcionar.');
                queryClient.invalidateQueries('configuracoes/chaves-api/fluxo');
            },
            onError: () => toast.error('Erro ao gerar a chave'),
        }
    );

    const revogarMutation = useMutation(
        async () => {
            await axios.delete('/api/configuracoes/chaves-api/fluxo', {
                withCredentials: true,
            });
        },
        {
            onSuccess: () => {
                toast.success('Chave revogada');
                queryClient.invalidateQueries('configuracoes/chaves-api/fluxo');
            },
            onError: () => toast.error('Erro ao revogar a chave'),
        }
    );

    const copiar = () => {
        if (!chave) return;
        navigator.clipboard.writeText(chave);
        toast('Chave copiada para a área de transferência!', {
            position: 'top-center',
            type: 'success',
            autoClose: 2000,
        });
    };

    const disableFields = isLoading || gerarMutation.isLoading || revogarMutation.isLoading;

    return (
        <VBox gap="24px">
            <VBox gap="8px">
                <h3>Chaves API</h3>
                <p style={{ color: 'var(--tc-color-gray-600)' }}>
                    Chave usada por fornecedores e integrações externas para disparar fluxos
                    diretamente via API, sem precisar de um usuário logado no sistema.
                </p>
            </VBox>

            <VBox gap="8px">
                <HBox stretch style={{ alignItems: 'flex-end', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                        <CampoTexto
                            label="Chave de disparo de fluxo"
                            value={isLoading ? '' : (chave || '')}
                            readOnly
                            placeholder={isLoading ? 'Carregando...' : 'Nenhuma chave gerada ainda'}
                            append={
                                chave ? (
                                    <Botao variant="none-secondary" icon={mdiContentCopy} onClick={copiar} title="Copiar" />
                                ) : undefined
                            }
                        />
                    </div>

                    <BotaoPerigoso
                        variant="outline-warning"
                        icon={gerarMutation.isLoading ? mdiLoading : mdiRefresh}
                        disabled={disableFields}
                        confirmation={
                            <div>
                                <p>
                                    {chave
                                        ? 'Gerar uma nova chave invalida imediatamente a chave atual — qualquer integração configurada com ela vai parar de funcionar.'
                                        : 'Uma nova chave será gerada para uso em integrações externas.'}
                                </p>
                                <p>Deseja continuar?</p>
                            </div>
                        }
                        onClick={() => gerarMutation.mutate()}
                    >
                        {chave ? 'Gerar nova' : 'Gerar chave'}
                    </BotaoPerigoso>

                    {chave && (
                        <BotaoPerigoso
                            variant="none-danger"
                            icon={mdiTrashCan}
                            disabled={disableFields}
                            title="Revogar chave"
                            confirmation={
                                <div>
                                    <p>
                                        Isso desativa imediatamente o disparo de fluxos por API para este tenant.
                                    </p>
                                    <p>Tem certeza que deseja revogar a chave?</p>
                                </div>
                            }
                            onClick={() => revogarMutation.mutate()}
                        />
                    )}
                </HBox>
                <small style={{ color: 'var(--tc-color-gray-600)' }}>
                    Envie esta chave no header <code>X-Api-Key</code> (ou <code>Authorization: Bearer &lt;chave&gt;</code>) ao
                    chamar <code>POST /api/dialogo/:id/start</code>, junto com o header <code>X-Tenant-Id</code> identificando esta organização.
                    O canal de disparo é o configurado no próprio fluxo (nó Início); só é preciso enviar <code>canal_id</code> no corpo para sobrescrevê-lo.
                </small>
            </VBox>

            <VBox gap="4px" style={{
                padding: '12px 16px',
                backgroundColor: 'var(--tc-color-gray-50)',
                borderRadius: '8px',
                border: '1px solid var(--tc-color-gray-200)',
            }}>
                <HBox gap="8px" style={{ alignItems: 'center' }}>
                    <Icon path={mdiKeyOutline} size={0.8} color="var(--tc-color-gray-600)" />
                    <strong>Exemplo de chamada</strong>
                </HBox>
                <pre style={{ margin: 0, overflowX: 'auto', fontSize: '12px' }}>
{`curl -X POST "https://SEU_DOMINIO/api/dialogo/{id_fluxo}/start" \\
  -H "X-Api-Key: ${chave || '<chave>'}" \\
  -H "X-Tenant-Id: SEU_TENANT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "canal_cliente_id": "5511999999999",
    "canal_cliente_nome": "Nome do cliente",
    "params": { "pedido": "12345" }
  }'`}
                </pre>
            </VBox>
        </VBox>
    );
};

export default SecaoChavesApi;
